import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { MONTH_NAME_OPTIONS } from '../../../../../shared/constants/month-options';
import { ControlErrorDirective } from '../../../../../shared/directive/control-error.directive';
import { RequiredLabelDirective } from '../../../../../shared/directive/required-label.directive';
import { ToastSeverity } from '../../../../../shared/enums/toast-severity.enum';
import { ApplicationFormDetails } from '../../../../../shared/models/application-form-details';
import { SelectOption } from '../../../../../shared/models/select-option';
import { ApplicationFormsApi } from '../../../../../shared/services/api/application-forms.api';
import { FormsUtils } from '../../../../../shared/utils/forms.utils';
import { APP_PATHS } from '../../../../../core/config/app-paths';
import { ApplicationFormEditFormBuilder } from './form-group/application-form-edit-form.builder';
import { ApplicationFormEditFormModel } from './form-group/application-form-edit-form.model';
import { getApplicationGroupDropdownItems } from '../../../../../shared/enums/application-group.enum';
import { MultiSelect } from 'primeng/multiselect';
import { getSwiadczeniobiorcyFilterDropdownItems } from '../../../../../shared/enums/application-list-swiadczeniobiorcy.enum';
import { getPrzedsiebiorcyFilterDropdownItems } from '../../../../../shared/enums/application-list-przedsiebiorcy.enum';
import { ValueOrDashPipe } from '../../../../../shared/pipes/value-or-dash.pipe';
import { Textarea } from 'primeng/textarea';

@Component({
  selector: 'app-admin-panel-application-form-edit',
  imports: [
    ControlErrorDirective,
    InputText,
    ReactiveFormsModule,
    RequiredLabelDirective,
    Select,
    Button,
    MultiSelect,
    ValueOrDashPipe,
    Textarea,
  ],
  templateUrl: './admin-panel-application-form-edit.html',
  styleUrl: './admin-panel-application-form-edit.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminPanelApplicationFormEdit implements OnInit {
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #applicationFormsApi = inject(ApplicationFormsApi);
  readonly #messageService = inject(MessageService);
  readonly #destroyRef = inject(DestroyRef);
  #isUpdatingRangeValidators = false;

  protected formGroup = signal<FormGroup<ApplicationFormEditFormModel> | null>(
    null,
  );
  protected applicationId = signal<number | null>(null);

  protected readonly areaOptions = getApplicationGroupDropdownItems();
  protected readonly visibleForAreaOptions = [
    ...getSwiadczeniobiorcyFilterDropdownItems(),
    ...getPrzedsiebiorcyFilterDropdownItems(),
  ];
  protected readonly booleanOptions: SelectOption<boolean>[] = [
    { label: 'Tak', value: true },
    { label: 'Nie', value: false },
  ];

  protected readonly monthOptions = MONTH_NAME_OPTIONS;

  ngOnInit(): void {
    const application = this.#route.snapshot.data[
      'application'
    ] as ApplicationFormDetails;

    this.applicationId.set(application.id);
    this.formGroup.set(
      ApplicationFormEditFormBuilder.createFormGroup(application),
    );
    this.#bindRangeValidation();
  }

  protected onSave(): void {
    const formGroup = this.formGroup();

    if (!formGroup || !FormsUtils.isReadyForSave(formGroup)) {
      this.#messageService.add({
        severity: ToastSeverity.ERROR,
        summary: 'Wystąpił błąd podczas walidacji formularza',
      });
      return;
    }

    const applicationId = this.applicationId();

    if (!applicationId) {
      return;
    }

    this.#applicationFormsApi
      .updateApplicationForm$(
        ApplicationFormEditFormBuilder.toModel(formGroup, applicationId),
      )
      .subscribe({
        next: () => {
          this.#messageService.add({
            severity: ToastSeverity.SUCCESS,
            summary: 'Poprawnie zapisano formularz wniosku.',
          });
          this.#navigateToDetails(applicationId);
        },
      });
  }

  protected onCancel(): void {
    const applicationId = this.applicationId();

    if (!applicationId) {
      return;
    }

    this.#navigateToDetails(applicationId);
  }

  protected isRangeInvalid(
    control: AbstractControl<unknown, unknown>,
  ): boolean {
    return (
      !!control.errors?.['rangeInvalid'] &&
      (control.dirty || control.touched)
    );
  }

  #bindRangeValidation(): void {
    const formGroup = this.formGroup();

    if (!formGroup) {
      return;
    }

    this.#watchControl(formGroup.controls.monthFrom);
    this.#watchControl(formGroup.controls.monthTo);
    this.#watchControl(formGroup.controls.dayFrom);
    this.#watchControl(formGroup.controls.dayTo);

    this.#updateRangeValidators();
  }

  #watchControl<T>(control: AbstractControl<T | null, T | null>): void {
    control.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.#updateRangeValidators());
  }

  #updateRangeValidators(): void {
    const formGroup = this.formGroup();

    if (!formGroup || this.#isUpdatingRangeValidators) {
      return;
    }

    this.#isUpdatingRangeValidators = true;

    try {
      ApplicationFormEditFormBuilder.updateRangeValidators(formGroup);
    } finally {
      this.#isUpdatingRangeValidators = false;
    }
  }

  #navigateToDetails(applicationId: number): void {
    this.#router.navigate([
      `/${APP_PATHS.admin.root}/${APP_PATHS.admin.applications}/${applicationId}/${APP_PATHS.admin.details}`,
    ]);
  }

  protected visibleForAreasLabels(): string {
    const values = this.formGroup()?.controls?.visibleForAreas?.value ?? [];

    return this.visibleForAreaOptions
      .filter((option) => values.includes(option.value))
      .map((option) => option.label)
      .join(', ');
  }
}
